export interface TimeData {
  dateFormatted: string;
  timeFormatted: string;
}

export type SupportedLanguage = 'es' | 'en' | 'ca';

/**
 * Get formatted time with localized month names from translations
 * @param monthsShort - Object with month translations from i18n (months_short)
 * @param sourceDate - Optional date to format. Defaults to current date/time
 * @returns Object with formatted date and time strings
 */
export function getFormattedTime(monthsShort: Record<string, string>, sourceDate: Date = new Date()): TimeData {
  const now = sourceDate;

  const day = now.getUTCDate().toString().padStart(2, '0');
  const monthIndex = now.getUTCMonth();

  // Map month index to month key
  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];
  const month = monthsShort[monthKeys[monthIndex]] || monthKeys[monthIndex].substring(0, 3).toUpperCase();

  const year = now.getUTCFullYear();
  const hours = now.getUTCHours().toString().padStart(2, '0');
  const minutes = now.getUTCMinutes().toString().padStart(2, '0');

  return {
    dateFormatted: `${day} ${month} ${year}`,
    timeFormatted: `${hours}:${minutes}`
  };
}

/**
 * Get the month abbreviation for a given month index
 * @param monthIndex - Month index (0-11)
 * @param monthsShort - Object with month translations from i18n (months_short)
 * @returns Month abbreviation
 */
export function getMonthAbbreviation(monthIndex: number, monthsShort: Record<string, string>): string {
  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];

  if (monthIndex < 0 || monthIndex > 11) {
    return '';
  }

  return monthsShort[monthKeys[monthIndex]] || '';
}

/**
 * Format a specific timestamp into date and time strings
 * @param timestamp - Unix timestamp in seconds
 * @param monthsShort - Optional object with month translations from i18n (months_short)
 * @returns Formatted date and time string (e.g., "16 FEB 2026 10:30")
 */
export function formatTimestamp(timestamp: number, monthsShort?: Record<string, string>): string {
  const date = new Date(timestamp * 1000);

  const day = date.getDate().toString().padStart(2, '0');
  const monthIndex = date.getMonth();

  // Map month index to month key
  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];
  const month = monthsShort
    ? monthsShort[monthKeys[monthIndex]] || monthKeys[monthIndex].substring(0, 3).toUpperCase()
    : monthKeys[monthIndex].substring(0, 3).toUpperCase();

  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

/**
 * Format a specific timestamp into time string only
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted time string (e.g., "10:30")
 */
export function formatTimestampToTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

/**
 * convertir 2026-06-12T07:09:26.000Z a 2026-06-12 07:09
 * @param val 2026-06-12T07:09:26.000Z
 * @returns 2026-06-12 07:09
 */
export function formatDataTime(val: string): string {
  return val.replace('T', ' ').slice(0, 16);
}

export function formatTimestampToMonthYear(): string {
  const date = new Date();

  const month = date.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12

  // Map month index to month key
  const year = date.getFullYear();

  return `${month}/${year}`;
}

/**
 * Format an ISO date string (e.g., "2026-06-30T15:36:39.035Z") to "30 JUN 2026 18:23 UTC"
 * @param isoString - ISO date string
 * @param monthsShort - Optional object with month translations from i18n (months_short)
 * @returns Formatted date string (e.g., "30 JUN 2026 18:23 UTC")
 */
export function formatISODate(isoString: string, monthsShort?: Record<string, string>): string {
  const date = new Date(isoString);

  const day = date.getUTCDate().toString().padStart(2, '0');
  const monthIndex = date.getUTCMonth();

  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];
  const month = monthsShort
    ? monthsShort[monthKeys[monthIndex]] || monthKeys[monthIndex].substring(0, 3).toUpperCase()
    : monthKeys[monthIndex].substring(0, 3).toUpperCase();

  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes} UTC`;
}
